import React, { useState, useEffect } from 'react';
import { Form, Input, Select, DatePicker, Button, Upload, Spin, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { getRequest, putRequest, uploadFile } from "../../../../service/index";
import { getEmployeeId } from "../../../../utils/localStorage";
import dayjs from 'dayjs';

const genderOptions = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

const bloodGroupOptions = [
  { value: "A+", label: "A+" },
  { value: "A-", label: "A-" },
  { value: "B+", label: "B+" },
  { value: "B-", label: "B-" },
  { value: "O+", label: "O+" },
  { value: "O-", label: "O-" },
  { value: "AB+", label: "AB+" },
  { value: "AB-", label: "AB-" },
];

const religionOptions = [
  { value: "Muslim", label: "Muslim" },
  { value: "Hindu", label: "Hindu" },
  { value: "Sikh", label: "Sikh" },
  { value: "Christian", label: "Christian" },
  { value: "Other", label: "Other" },
];

const qualificationOptions = [
  { value: "Matric", label: "Matric" },
  { value: "Inter", label: "Inter" },
  { value: "Bachelor", label: "Bachelor" },
  { value: "Master", label: "Master" },
  { value: "PHD", label: "PHD" },
];

const maritalStatusOptions = [
  { value: "Single", label: "Single" },
  { value: "Married", label: "Married" },
  { value: "Divorced", label: "Divorced" },
  { value: "Widow", label: "Widow" },
];

const RELATIONSHIP_OPTIONS = [
  { value: "wife", label: "Wife" },
  { value: "husband", label: "Husband" }, 
  { value: "father", label: "Father" },
  { value: "mother", label: "Mother" },
  { value: "brother", label: "Brother" },
  { value: "sister", label: "Sister" },
  { value: "child", label: "Child" },
  { value: "uncle", label: "Uncle" },
];

const paymentOptions = [
  { value: "bank", label: "Bank Transfer" },
  { value: "cash", label: "Cash" },
  { value: "cheque", label: "Cheque" },
];

const MyInfo = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [employeeData, setEmployeeData] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const employeeId = getEmployeeId();
  const paymentType = Form.useWatch('paymentType', form);

  useEffect(() => {
    fetchEmployeeData();
  }, []);

  const fetchEmployeeData = async () => {
    try {
      setLoading(true);
      const response = await getRequest(`/api/employees/${employeeId}`);
      if (response?.employee) {
        setEmployeeData(response.employee);
        form.setFieldsValue({
          employeeId: response.employee.employee_code,
          name: `${response.employee.firstName} ${response.employee.lastName}`,
          fatherName: response.employee.fatherName,
          gender: response.employee.gender,
          bloodGroup: response.employee.bloodGroup,
          qualification: response.employee.qualification,
          address: response.employee.permanent_address,
          temporaryAddress: response.employee.temporary_address,
          email: response.employee.emailAddress,
          dob: response.employee.dob ? dayjs(response.employee.dob) : null,
          maritalStatus: response.employee.maritalStatus,
          religion: response.employee.religion,
          nationalId: response.employee.nationalId,
          contact_number: response.employee.contact_number,
          eobi: response.employee.EOBI,
          passportNo: response.employee.passportNo,
          ntn: response.employee.NTN,
          nationality: response.employee.nationality,
          country: response.employee.country,
          city: response.employee.city,
          zipCode: response.employee.zipCode,
          phone: response.employee.contact_number,
          status: response.employee.status,
          employment: response.employee.employee_type,
          probationMonths: response.employee.probation_months,
          startDate: response.employee.contract_start_date ? dayjs(response.employee.contract_start_date) : null,
          endDate: response.employee.contract_end_date ? dayjs(response.employee.contract_end_date) : null,
          payType: response.employee.salary_type,
          shift: response.employee.shift?.shiftName,
          hireDate: response.employee.hire_date ? dayjs(response.employee.hire_date) : null,
          workLocation: response.employee.location?.locationName,
          designation: response.employee.designation?.designationName,
          department: response.employee.department?.name,
          subDepartment: response.employee.department?.subDepartments?.find(sub => sub._id === response.employee.sub_department)?.name,
          lineManager: response.employee.reporting_to,
          medical: response.employee.medical,
          travel: response.employee.travel,
          bonus: response.employee.bouns,
          paymentType: response.employee.payment_type,
          bankName: response.employee.bank_name,
          accountTitle: response.employee.account_title,
          accountNumber: response.employee.account_number,
          ibnNumber: response.employee.ibn_number,
          payPeriod: 'Monthly Payroll',
          baseSalary: `RS ${response.employee.monthly_salary}`,
          allowances: `RS ${response.employee.allowance}`,
          contributions: `RS ${response.employee.contribution}`,
          deductions: `RS ${response.employee.deduction}`,
          netSalary: `RS ${response.employee.overAll_salary}`,
          perDaySalary: `RS ${response.employee.perday_salary}`,
          perHourSalary: `RS ${response.employee.perhour_salary}`,
          emergencyContact: response.employee.emergency_contact,
          emergencyName: `${response.employee.emergency_firstName} ${response.employee.emergency_lastName}`,
          relationshipType: response.employee.relationship_type,
          remarks: response.employee.remarks
        });
      }
    } catch (error) {
      console.error('Error fetching employee data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setUploadedImageUrl(null);
    fetchEmployeeData();
  };



  const handleImageUpload = async (file) => {
    try {
      const response = await uploadFile(file);
      if (response.url) {
        setUploadedImageUrl(response.url);
        message.success('Image uploaded successfully');
      } else {
        message.error('Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      message.error('Failed to upload image');
    }
  };

  const buildEditablePayload = (values) => {
    const payload = {
      firstName: values.name?.split(' ')[0],
      lastName: values.name?.split(' ').slice(1).join(' '),
      fatherName: values.fatherName,
      gender: values.gender,
      bloodGroup: values.bloodGroup,
      qualification: values.qualification,
      permanent_address: values.address,
      temporary_address: values.temporaryAddress,
      dob: values.dob?.toISOString(),
      maritalStatus: values.maritalStatus,
      religion: values.religion,
      nationalId: values.nationalId,
      contact_number: values.contact_number,
      EOBI: values.eobi,
      passportNo: values.passportNo,
      NTN: values.ntn,
      nationality: values.nationality,
      emergency_firstName: values.emergencyName?.split(' ')[0],
      emergency_lastName: values.emergencyName?.split(' ').slice(1).join(' '),
      emergency_contact: values.emergencyContact,
      relationship_type: values.relationshipType,
      payment_type: values.paymentType,
    };

    if (uploadedImageUrl) {
      payload.image = uploadedImageUrl;
    }

    if (values.paymentType === 'bank') {
      payload.bank_name = values.bankName;
      payload.account_title = values.accountTitle;
      payload.account_number = values.accountNumber;
      payload.ibn_number = values.ibnNumber;
    }

    return payload;
  };

  const handleSave = async (values) => {
    try {
      setLoading(true);

      const formattedData = buildEditablePayload(values);
      const response = await putRequest(`/api/employees?employeeId=${employeeId}`, formattedData);
      
      if (response.message === 'Employee updated successfully (some related data could not be loaded)') {
        message.success('Profile updated successfully');
        setIsEditing(false);
        setUploadedImageUrl(null);
        fetchEmployeeData();
      } else {
        message.error(response.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating employee data:', error);
      message.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">My Profile</h1>
        <div className="flex justify-end gap-4 mt-6">
          {!isEditing ? (
            <Button className='bg-primary' type="primary" onClick={handleEdit}>
              Edit Profile
            </Button>
          ) : (
            <>
              <Button 
                className='bg-primary' 
                type="primary" 
                onClick={() => form.validateFields().then(handleSave)}
              >
                Save Changes
              </Button>
              <Button onClick={handleCancel}>Cancel</Button>
            </>
          )}
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
      >
        <div className="grid grid-cols-1 gap-6">
          <div className="grid grid-cols-1 gap-6">
            <div className="flex items-center gap-6">
              <div
                className="rounded-full border-4 border-white shadow-lg"
                style={{
                  width: 180,
                  height: 180,
                  background: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                <img
                  src={uploadedImageUrl || employeeData?.image || 'https://via.placeholder.com/180'}
                  alt="Profile"
                  className="rounded-full object-cover"
                  style={{
                    width: 172,
                    height: 172,
                    objectFit: 'cover',
                    background: '#f3f3f3',
                  }}
                />
                {isEditing && (
                  <div
                    className="absolute"
                    style={{
                      top: 10,
                      right: 10,
                      zIndex: 10,
                      background: '#fff',
                      borderRadius: '50%',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      width: 36,
                      height: 36,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Upload 
                      showUploadList={false}
                      beforeUpload={(file) => {
                        handleImageUpload(file);
                        return false;
                      }}
                    >
                      <Button
                        icon={<UploadOutlined />}
                        size="small"
                        style={{
                          border: 'none',
                          background: 'transparent',
                          boxShadow: 'none',
                          padding: 0,
                        }}
                      />
                    </Upload>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold mb-4">Basic Information</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Form.Item name="employeeId" label="Employee ID">
                    <Input disabled={true} />
                  </Form.Item>
                  <Form.Item name="name" label="Full Name">
                    <Input disabled={!isEditing} />
                  </Form.Item>
                  <Form.Item name="fatherName" label="Father's Name">
                    <Input disabled={!isEditing} />
                  </Form.Item>
                  <Form.Item name="gender" label="Gender">
                    <Select disabled={!isEditing} options={genderOptions} />
                  </Form.Item>
                  <Form.Item name="bloodGroup" label="Blood Group">
                    <Select disabled={!isEditing} options={bloodGroupOptions} />
                  </Form.Item>
                  <Form.Item name="qualification" label="Qualification">
                    <Select disabled={!isEditing} options={qualificationOptions} />
                  </Form.Item>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-semibold mb-4">Personal Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    <Form.Item name="dob" label="Date of Birth">
                      <DatePicker disabled={!isEditing} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="maritalStatus" label="Marital Status">
                      <Select disabled={!isEditing} options={maritalStatusOptions} />
                    </Form.Item>
                    <Form.Item name="religion" label="Religion">
                      <Select disabled={!isEditing} options={religionOptions} />
                    </Form.Item>
                    <Form.Item name="contact_number" label="Contact Number">
                      <Input disabled={!isEditing} />
                    </Form.Item>
                    <Form.Item name="nationalId" label="National ID">
                      <Input disabled={!isEditing} />
                    </Form.Item>
                    <Form.Item name="eobi" label="EOBI">
                      <Input disabled={!isEditing} />
                    </Form.Item>
                    <Form.Item name="passportNo" label="Passport No">
                      <Input disabled={!isEditing} />
                    </Form.Item>
                    <Form.Item name="ntn" label="NTN">
                      <Input disabled={!isEditing} />
                    </Form.Item>
                    <Form.Item name="nationality" label="Nationality">
                      <Input disabled={!isEditing} />
                    </Form.Item>
                  </div>
                  <div className="mt-4">
                    <Form.Item name="address" label="Permanent Address" className="w-full">
                      <Input.TextArea disabled={!isEditing} rows={3} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="temporaryAddress" label="Temporary Address" className="w-full">
                      <Input.TextArea disabled={!isEditing} rows={3} style={{ width: '100%' }} />
                    </Form.Item>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-4">Emergency Contact</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <Form.Item name="emergencyName" label="Emergency Contact Name">
                <Input disabled={!isEditing} />
              </Form.Item>
              <Form.Item name="emergencyContact" label="Emergency Contact Number">
                <Input disabled={!isEditing} />
              </Form.Item>
              <Form.Item name="relationshipType" label="Relationship">
                <Select disabled={!isEditing} options={RELATIONSHIP_OPTIONS} />
              </Form.Item>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-4">Employment & Payment Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <Form.Item name="paymentType" label="Salary Payment Method">
                <Select
                  disabled={!isEditing}
                  options={paymentOptions}
                  placeholder="Select Payment Method"
                />
              </Form.Item>
              {(paymentType === 'bank' || employeeData?.payment_type === 'bank') && (
                <>
                  <Form.Item name="bankName" label="Bank Name">
                    <Input disabled={!isEditing} placeholder="Bank Name" />
                  </Form.Item>
                  <Form.Item name="accountTitle" label="Account Title">
                    <Input disabled={!isEditing} placeholder="Account Title" />
                  </Form.Item>
                  <Form.Item name="accountNumber" label="Account Number">
                    <Input disabled={!isEditing} placeholder="Account Number" />
                  </Form.Item>
                  <Form.Item name="ibnNumber" label="IBAN Number">
                    <Input disabled={!isEditing} placeholder="IBAN Number" />
                  </Form.Item>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-[#dbfce7] p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-semibold mb-4">Official Information</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-600">Status</p>
                  <p className="font-medium">{employeeData?.status}</p>
                </div>
                <div>
                  <p className="text-gray-600">Employment Type</p>
                  <p className="font-medium">{employeeData?.employee_type}</p>
                </div>
                <div>
                  <p className="text-gray-600">Probation Period</p>
                  <p className="font-medium">{employeeData?.probation_months} months</p>
                </div>
                <div>
                  <p className="text-gray-600">Pay Type</p>
                  <p className="font-medium">{employeeData?.salary_type}</p>
                </div>
                <div>
                  <p className="text-gray-600">Shift</p>
                  <p className="font-medium">{employeeData?.shift?.shiftName}</p>
                </div>
                <div>
                  <p className="text-gray-600">Work Location</p>
                  <p className="font-medium">{employeeData?.location?.locationName}</p>
                </div>
                <div>
                  <p className="text-gray-600">Designation</p>
                  <p className="font-medium">{employeeData?.designation?.designationName}</p>
                </div>
                <div>
                  <p className="text-gray-600">Department</p>
                  <p className="font-medium">{employeeData?.department?.name}</p>
                </div>
                <div>
                  <p className="text-gray-600">Sub Department</p>
                  <p className="font-medium">
                    {employeeData?.department?.subDepartments?.find(sub => sub._id === employeeData?.sub_department)?.name}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Line Manager</p>
                  <p className="font-medium">{employeeData?.reporting_to || 'Not Assigned'}</p>
                </div>
              </div>
            </div>

            <div className="bg-[#ffe2e2] p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-semibold mb-4">Salary Information</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-600">Pay Period</p>
                  <p className="font-medium">Monthly Payroll</p>
                </div>
                <div>
                  <p className="text-gray-600">Base Salary</p>
                  <p className="font-medium">RS {employeeData?.monthly_salary}</p>
                </div>
                <div>
                  <p className="text-gray-600">Allowances</p>
                  <p className="font-medium">RS {employeeData?.allowance}</p>
                </div>
                <div>
                  <p className="text-gray-600">Contributions</p>
                  <p className="font-medium">RS {employeeData?.contribution}</p>
                </div>
                <div>
                  <p className="text-gray-600">Deductions</p>
                  <p className="font-medium">RS {employeeData?.deduction}</p>
                </div>
                <div>
                  <p className="text-gray-600">Net Salary</p>
                  <p className="font-medium">RS {employeeData?.overAll_salary}</p>
                </div>
                <div>
                  <p className="text-gray-600">Per Day Salary</p>
                  <p className="font-medium">RS {employeeData?.perday_salary}</p>
                </div>
                <div>
                  <p className="text-gray-600">Per Hour Salary</p>
                  <p className="font-medium">RS {employeeData?.perhour_salary}</p>
                </div>
              </div>
            </div>
          </div>


        </div>
      </Form>
    </div>
  );
};

export default MyInfo;